// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, Fade, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ClearPricingOverrideThunk, GetPricingOverrideConfigThunk, GetPricingOverrideHistoryThunk } from 'Features/admin/adminThunk';
import LoadingSpinner from 'Components/LoadingSpinner';
import { PricingOverrideDialog } from '../PricingOverrideDialog';
import stylesDef from './styles';

interface PricingOverrideSectionProps {
  order: IOrder;
}

const formatMoney = (amount?: number) => {
  if (amount == null) return 'N/A';
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const PricingOverrideSection = ({ order }: PricingOverrideSectionProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const pricingAccess = useAppSelector((state) => state.auth.user?.pricingAccess);
  const config = useAppSelector((state) => state.admin.pricingOverrideConfig);

  const [showDialog, setShowDialog] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<IPricingOverrideHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (pricingAccess) dispatch(GetPricingOverrideConfigThunk());
  }, [pricingAccess, dispatch]);

  if (!pricingAccess) return null;

  const activeOverride = order.pricingOverride && order.pricingOverride.overrideType ? order.pricingOverride : null;
  const typeConfig = activeOverride && config ? config.types.find((t) => t.key === activeOverride.overrideType) : undefined;

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) {
      setHistoryLoading(true);
      const result = await dispatch(GetPricingOverrideHistoryThunk(order._id));
      if (result.success) setHistoryEntries(result.data.history);
      setHistoryLoading(false);
    }
  };

  const handleClear = async () => {
    setIsClearing(true);
    setClearError('');
    const result = await dispatch(ClearPricingOverrideThunk(order._id));
    setIsClearing(false);
    if (result.success) {
      setShowClearConfirm(false);
      setShowHistory(false);
    } else {
      setClearError(result.message || 'Unable to clear the pricing override');
    }
  };

  const valueRows = activeOverride
    ? Object.keys(activeOverride.values).map((key) => {
      const field = typeConfig?.fields.find((f) => f.key === key);
      const value = activeOverride.values[key];
      const display = field?.adornment === '$' && typeof value === 'number' ? formatMoney(value) : String(value);
      return { key, label: field?.label || key, display };
    })
    : [];

  return (
    <Box sx={styles.sectionWrapper}>
      <Typography sx={styles.sectionTitle}>Pricing Override</Typography>
      {!activeOverride ? (
        <Box sx={styles.summaryBlock}>
          <Typography sx={styles.mutedText}>Org default pricing</Typography>
          <Button variant="outlined" size="small" disabled={!config} onClick={() => setShowDialog(true)}>
            Set Pricing Override
          </Button>
        </Box>
      ) : (
        <Box sx={styles.summaryBlock}>
          <Typography sx={styles.overrideTypeText}>{typeConfig?.label || activeOverride.overrideType}</Typography>
          {valueRows.map((row) => (
            <Typography key={row.key} sx={styles.valueRow}>
              {row.label}
              {': '}
              {row.display}
            </Typography>
          ))}
          <Typography sx={styles.computedCostText}>
            Computed cost:
            {' '}
            {formatMoney(activeOverride.computedCost)}
          </Typography>
          <Typography sx={styles.mutedText}>
            {`Set by ${activeOverride.setBy || 'N/A'}${activeOverride.setAt ? ` on ${moment.utc(activeOverride.setAt).format('MMM D, YYYY')}` : ''}`}
          </Typography>
          {activeOverride.updatedBy && (
            <Typography sx={styles.mutedText}>
              {`Updated by ${activeOverride.updatedBy}${activeOverride.updatedAt ? ` on ${moment.utc(activeOverride.updatedAt).format('MMM D, YYYY')}` : ''}`}
            </Typography>
          )}
          {activeOverride.notes && <Typography sx={styles.notesText}>{`Notes: ${activeOverride.notes}`}</Typography>}
          <Box sx={styles.buttonRow}>
            <Button variant="outlined" size="small" disabled={!config} onClick={() => setShowDialog(true)}>Edit</Button>
            <Button variant="outlined" size="small" color="error" onClick={() => setShowClearConfirm(true)}>Clear</Button>
            <Button variant="text" size="small" onClick={toggleHistory}>{showHistory ? 'Hide History' : 'History'}</Button>
          </Box>
        </Box>
      )}
      {showHistory && (
        <LoadingSpinner loading={historyLoading}>
          <ul style={styles.historyList}>
            {historyEntries.map((entry) => (
              <li key={entry._id}>
                <Typography sx={styles.historyItem}>
                  {`${entry.action} · ${entry.overrideType} · ${formatMoney(entry.newCostToCustomer)} · credits ${entry.creditDelta != null && entry.creditDelta >= 0 ? '+' : ''}${entry.creditDelta ?? 0} · ${entry.createdBy} · ${moment.utc(entry.createdAt).format('MMM D, YYYY HH:mm')}`}
                </Typography>
                {entry.notes && <Typography sx={styles.historyNotes}>{entry.notes}</Typography>}
              </li>
            ))}
            {!historyLoading && historyEntries.length === 0 && <Typography sx={styles.mutedText}>No history yet</Typography>}
          </ul>
        </LoadingSpinner>
      )}
      <Dialog open={showClearConfirm} onClose={() => setShowClearConfirm(false)} closeAfterTransition={false}>
        <Box sx={styles.confirmContainer}>
          <Typography variant="h4" sx={styles.sectionTitle}>Clear pricing override?</Typography>
          <Typography>
            {`This restores org-default pricing and reverses ${activeOverride?.appliedCreditDelta != null && activeOverride.appliedCreditDelta >= 0 ? '+' : ''}${activeOverride?.appliedCreditDelta ?? 0} credits applied by this override.`}
          </Typography>
          {clearError && <Typography sx={styles.errorText}>{clearError}</Typography>}
          <Box sx={styles.buttonRow}>
            <Button variant="text" sx={styles.closeButton} onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleClear} disabled={isClearing}>
              <Fade in={isClearing}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!isClearing}>
                <span>Clear Override</span>
              </Fade>
            </Button>
          </Box>
        </Box>
      </Dialog>
      {config && (
        <PricingOverrideDialog
          open={showDialog}
          setOpen={setShowDialog}
          order={order}
          config={config}
          existingOverride={activeOverride}
        />
      )}
    </Box>
  );
};
